import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/api';
import MyPredictionsSummary from '../components/MyPredictionsSummary';
import TeamWidget from '../components/TeamWidget';
import { getTeamLogoUrl } from '../utils/logoUrl';
import { formatDate } from '../utils/dateUtils';
import { determineActiveMatchGroup } from '../utils/matchGroupUtils';
import { invalidateUpcomingMatchesCache, invalidateSeasonsCache } from '../utils/cacheUtils';
import ScoreInput from '../components/ScoreInput';
import EmptyStateCard from '../components/EmptyStateCard';
import BankerButton from '../components/BankerButton';
import { getBankerMatchIdInWeek } from '../utils/bankerUtils';
import ScoredMatchSummary, { MissedMatchSummary, PendingPredictionSummary } from '../components/ScoredMatchSummary';
import { getPredictionOutcome, getOutcomeCardClasses, hasMatchResult as matchHasResult, isScoredOutcome, getOutcomeAccentColor, getOutcomeMutedTextColor } from '../utils/predictionOutcomeUtils';
import { usePredictionAutoSubmit } from '../hooks/usePredictionAutoSubmit';
import TeamFormIndicator from '../components/TeamFormIndicator';
import { useTeamForms } from '../hooks/useTeamForms';

interface Team {
  id: string;
  name: string;
  shortName?: string;
  logoUrl?: string;
}

interface Match {
  id: string;
  homeTeam: Team;
  awayTeam: Team;
  matchDate: string;
  matchGroup: { id: string; name: string };
  venue?: string;
  isCup?: boolean;
  predictionsClose: string;
  status: string;
  homeGoals?: number;
  awayGoals?: number;
}

interface SeasonMatchData {
  matchGroups: MatchGroup[];
  matches: Match[];
  currentMatchGroup: MatchGroup | null;
}

interface MatchWeekEntry {
  match: Prediction['match'];
  prediction: Prediction | null;
}

interface Season {
  id: string;
  name: string;
  order?: number;
}

interface MatchGroup {
  id: string;
  name: string;
  matchWeek: number;
}

interface Prediction {
  id: string;
  match: {
    id: string;
    homeTeam: {
      id: string;
      name: string;
      shortName?: string;
      logoUrl?: string;
    };
    awayTeam: {
      id: string;
      name: string;
      shortName?: string;
      logoUrl?: string;
    };
    matchDate: string;
    predictionsClose: string;
    homeGoals?: number;
    awayGoals?: number;
    status: string;
  };
  season: Season;
  matchGroup: MatchGroup;
  homeGoals: number;
  awayGoals: number;
  isBanker: boolean;
  points?: number;
  exactScorePoints?: number;
  correctResultPoints?: number;
  bankerBonusPoints?: number;
  pointsBreakdown?: {
    exactScore: number;
    correctResult: number;
    bankerBonus: number;
  };
  isScored: boolean;
  submittedAt: string;
}

const categorizeMatchGroups = (matchGroups: MatchGroup[], currentMatchGroup: MatchGroup | null) => {
  if (!currentMatchGroup || matchGroups.length === 0) {
    return {
      current: null,
      previous: null,
      old: [...matchGroups].sort((a, b) => a.matchWeek - b.matchWeek),
      future: [] as MatchGroup[],
    };
  }

  const currentWeek = currentMatchGroup.matchWeek;
  const sortedGroups = [...matchGroups].sort((a, b) => a.matchWeek - b.matchWeek);

  return {
    current: currentMatchGroup,
    previous: sortedGroups.find(group => group.matchWeek === currentWeek - 1) || null,
    old: sortedGroups.filter(group => group.matchWeek < currentWeek - 1),
    future: sortedGroups.filter(group => group.matchWeek > currentWeek),
  };
};

const SkeletonBar: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`animate-pulse rounded bg-gray-200 dark:bg-gray-700 ${className}`} />
);

const MATCH_CARD_SKELETON_COUNT = 4;

const MatchCardSkeleton: React.FC = () => (
  <div className="card rounded-lg p-3 md:p-4 bg-gray-50 dark:bg-gray-800/50">
    <div className="flex justify-between mb-3">
      <SkeletonBar className="h-4 w-24" />
      <SkeletonBar className="h-5 w-16 rounded-full" />
    </div>
    <div className="grid grid-cols-3 gap-3 items-center mb-4">
      <div className="text-center space-y-1.5">
        <SkeletonBar className="h-8 w-8 mx-auto" />
        <SkeletonBar className="h-3.5 w-16 mx-auto" />
      </div>
      <SkeletonBar className="h-5 w-6 mx-auto" />
      <div className="text-center space-y-1.5">
        <SkeletonBar className="h-8 w-8 mx-auto" />
        <SkeletonBar className="h-3.5 w-16 mx-auto" />
      </div>
    </div>
    <div className="grid grid-cols-3 gap-3 items-center">
      <SkeletonBar className="h-8 w-12 mx-auto" />
      <SkeletonBar className="h-4 w-3 mx-auto" />
      <SkeletonBar className="h-8 w-12 mx-auto" />
    </div>
  </div>
);

const MyPredictionsSkeleton: React.FC = () => (
  <div className="min-h-screen overflow-x-hidden">
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 w-full min-w-0">
      <div className="mb-8">
        <Link to="/" className="mb-4 inline-block" style={{ color: 'var(--highlight)' }}>
          ← Back to Home
        </Link>
        <SkeletonBar className="h-9 w-56 max-w-full mb-3" />
        <SkeletonBar className="h-5 w-72 max-w-full" />
      </div>

      <div className="mb-6 card rounded-lg bg-gray-50 dark:bg-gray-800/50 p-6">
        <div className="flex h-10 items-end gap-2 mb-0">
          <SkeletonBar className="h-10 w-36 rounded-t-lg" />
          <SkeletonBar className="h-10 w-40 rounded-t-lg opacity-60" />
        </div>
        <div className="rounded-lg border p-6" style={{ borderColor: 'var(--border)' }}>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="text-center space-y-2">
                <SkeletonBar className="h-8 w-16 mx-auto" />
                <SkeletonBar className="h-4 w-20 mx-auto" />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mb-6 flex gap-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonBar key={i} className="h-9 w-32 flex-shrink-0 rounded-md" />
        ))}
      </div>

      <div className="space-y-6">
        <div className="flex gap-2 mb-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <SkeletonBar key={i} className="h-9 w-28 flex-shrink-0 rounded-md" />
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
          {Array.from({ length: MATCH_CARD_SKELETON_COUNT }).map((_, i) => (
            <MatchCardSkeleton key={i} />
          ))}
        </div>
      </div>
    </div>
  </div>
);

const MyPredictions: React.FC = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const urlSeasonId = searchParams.get('season');
  const urlMatchGroupId = searchParams.get('matchGroup');
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSeason, setSelectedSeason] = useState<string>('all');
  const [predictionForms, setPredictionForms] = useState<Record<string, { homeGoals: string; awayGoals: string; isBanker: boolean }>>({});
  const [submitting, setSubmitting] = useState<Record<string, boolean>>({});
  const [seasonMatchData, setSeasonMatchData] = useState<Record<string, SeasonMatchData>>({});
  const [selectedMatchGroupBySeason, setSelectedMatchGroupBySeason] = useState<Record<string, string>>({});
  const [showPreviousMatchweeksBySeason, setShowPreviousMatchweeksBySeason] = useState<Record<string, boolean>>({});
  const [matchGroupScrollArrows, setMatchGroupScrollArrows] = useState<Record<string, { left: boolean; right: boolean }>>({});
  const [seasonTabsScrollLeft, setSeasonTabsScrollLeft] = useState(0);

  // Handle season tabs scroll
  const handleSeasonTabsScroll = (e: React.UIEvent<HTMLDivElement>) => {
    setSeasonTabsScrollLeft(e.currentTarget.scrollLeft);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Clear seasons cache to ensure fresh data with correct order
        invalidateSeasonsCache();
        
        // Fetch all predictions
        const predictionsResponse = await apiClient.get('/predictions');
        setPredictions(predictionsResponse.data);
        
        // Initialize prediction forms with existing predictions
        const initialForms: Record<string, { homeGoals: string; awayGoals: string; isBanker: boolean }> = {};
        predictionsResponse.data.forEach((prediction: Prediction) => {
          initialForms[prediction.match.id] = {
            homeGoals: prediction.homeGoals.toString(),
            awayGoals: prediction.awayGoals.toString(),
            isBanker: prediction.isBanker
          };
        });
        setPredictionForms(initialForms);
        
        // Fetch seasons for filter
        const seasonsResponse = await apiClient.get('/seasons');
        setSeasons(seasonsResponse.data);
        
        // Set default season to the first ordered season (e.g., Premier League)
        if (seasonsResponse.data.length > 0) {
          const sortedSeasons = [...seasonsResponse.data].sort((a, b) => (a.order || 0) - (b.order || 0));
          const seasonFromUrl = urlSeasonId && seasonsResponse.data.some((s: Season) => s.id === urlSeasonId)
            ? urlSeasonId
            : null;
          setSelectedSeason(seasonFromUrl || sortedSeasons[0].id);
        }
        
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load predictions');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user]);

  // Fetch match groups and fixtures for each season
  useEffect(() => {
    if (seasons.length === 0) return;

    const fetchSeasonMatchData = async () => {
      const data: Record<string, SeasonMatchData> = {};
      const initialSelected: Record<string, string> = {};

      await Promise.all(
        seasons.map(async (season) => {
          try {
            const [groupsResponse, matchesResponse] = await Promise.all([
              apiClient.get(`/seasons/${season.id}/match-groups`),
              apiClient.get(`/matches?season=${season.id}`),
            ]);

            const matchGroups = groupsResponse.data;
            const matches = matchesResponse.data;
            const currentMatchGroup = determineActiveMatchGroup(matchGroups, matches, new Date())
              || matchGroups[0]
              || null;

            data[season.id] = { matchGroups, matches, currentMatchGroup };
            if (currentMatchGroup) {
              initialSelected[season.id] = currentMatchGroup.id;
            }
          } catch {
            // Season may not have matchweek data yet
          }
        })
      );

      setSeasonMatchData(data);
      setSelectedMatchGroupBySeason(prev => {
        const next = { ...prev };
        Object.entries(initialSelected).forEach(([seasonId, groupId]) => {
          if (!next[seasonId]) {
            next[seasonId] = groupId;
          }
        });
        if (urlSeasonId && urlMatchGroupId && data[urlSeasonId]?.matchGroups.some((g) => g.id === urlMatchGroupId)) {
          next[urlSeasonId] = urlMatchGroupId;
        }
        return next;
      });

      setPredictionForms(prev => {
        const updated = { ...prev };
        Object.values(data).forEach(({ matches }) => {
          matches.forEach(match => {
            if (!updated[match.id]) {
              updated[match.id] = { homeGoals: '', awayGoals: '', isBanker: false };
            }
          });
        });
        return updated;
      });
    };

    fetchSeasonMatchData();
  }, [seasons]);

  const checkMatchGroupScrollPosition = (seasonId: string) => {
    const container = document.getElementById(`matchgroup-tabs-${seasonId}`);
    if (!container) return;

    setMatchGroupScrollArrows(prev => ({
      ...prev,
      [seasonId]: {
        left: container.scrollLeft > 0,
        right: container.scrollLeft < container.scrollWidth - container.clientWidth,
      },
    }));
  };

  const scrollToActiveMatchGroupTab = (seasonId: string) => {
    const container = document.getElementById(`matchgroup-tabs-${seasonId}`);
    const activeButton = container?.querySelector('.btn-primary') as HTMLElement;

    if (container && activeButton) {
      const scrollLeft = activeButton.offsetLeft - container.clientWidth / 2 + activeButton.clientWidth / 2;
      container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      setTimeout(() => checkMatchGroupScrollPosition(seasonId), 300);
    }
  };

  useEffect(() => {
    Object.keys(selectedMatchGroupBySeason).forEach(seasonId => {
      setTimeout(() => {
        checkMatchGroupScrollPosition(seasonId);
        scrollToActiveMatchGroupTab(seasonId);
      }, 100);
    });
  }, [selectedMatchGroupBySeason, seasonMatchData]);

  useEffect(() => {
    Object.entries(selectedMatchGroupBySeason).forEach(([seasonId, groupId]) => {
      const data = seasonMatchData[seasonId];
      if (!data) return;

      const categorized = categorizeMatchGroups(data.matchGroups, data.currentMatchGroup);
      if (categorized.old.some(group => group.id === groupId)) {
        setShowPreviousMatchweeksBySeason(prev => ({ ...prev, [seasonId]: true }));
      }
    });
  }, [selectedMatchGroupBySeason, seasonMatchData]);


  const getMatchStatusForEntry = (entry: MatchWeekEntry): string => {
    const now = new Date();
    const matchDate = new Date(entry.match.matchDate);
    const predictionsClose = new Date(entry.match.predictionsClose);

    if (now < predictionsClose) {
      return 'upcoming';
    } else if (now < matchDate) {
      return 'predictions-closed';
    } else {
      return entry.prediction?.isScored ? 'scored' : 'played';
    }
  };

  const hasMatchResult = (match: MatchWeekEntry['match']): boolean =>
    matchHasResult(match.homeGoals, match.awayGoals);

  const getMatchCardStatusPill = (
    match: MatchWeekEntry['match'],
    prediction: Prediction | null,
    status: string
  ): React.ReactNode => {
    if (prediction?.isScored) {
      return null;
    }

    if (prediction) {
      return (
        <span className="px-2 py-1 text-xs rounded-full font-medium whitespace-nowrap bg-highlight text-black">
          {status === 'upcoming' ? '⚽️ Predicted' : 'Locked in'}
        </span>
      );
    }

    if (!hasMatchResult(match) && status !== 'upcoming') {
      return (
        <span className="px-2 py-1 text-xs rounded-full font-medium whitespace-nowrap bg-highlight text-black">
          🔒 Locked
        </span>
      );
    }

    return null;
  };

  const buildMatchWeekEntries = (groupPredictions: Prediction[], allMatches?: Match[]): MatchWeekEntry[] => {
    if (!allMatches) {
      return groupPredictions.map(prediction => ({
        match: prediction.match,
        prediction,
      }));
    }

    const predictionByMatchId = new Map(groupPredictions.map(prediction => [prediction.match.id, prediction]));

    return allMatches.map(match => ({
      match: {
        id: match.id,
        homeTeam: {
          id: match.homeTeam.id,
          name: match.homeTeam.name,
          shortName: match.homeTeam.shortName,
          logoUrl: match.homeTeam.logoUrl,
        },
        awayTeam: {
          id: match.awayTeam.id,
          name: match.awayTeam.name,
          shortName: match.awayTeam.shortName,
          logoUrl: match.awayTeam.logoUrl,
        },
        matchDate: match.matchDate,
        predictionsClose: match.predictionsClose,
        homeGoals: match.homeGoals,
        awayGoals: match.awayGoals,
        status: match.status,
      },
      prediction: predictionByMatchId.get(match.id) || null,
    }));
  };

  const seasonFilteredPredictions = predictions.filter(prediction =>
    selectedSeason === 'all' || prediction.season.id === selectedSeason
  );

  const visibleSeasons = useMemo(() => {
    const sorted = [...seasons].sort((a, b) => (a.order || 0) - (b.order || 0));
    if (selectedSeason === 'all') {
      return sorted.filter(season => seasonMatchData[season.id]);
    }
    return sorted.filter(season => season.id === selectedSeason && seasonMatchData[season.id]);
  }, [seasons, selectedSeason, seasonMatchData]);

  const upcomingMatchIds = useMemo(() => {
    const ids: string[] = [];
    const now = new Date();

    visibleSeasons.forEach(season => {
      const data = seasonMatchData[season.id];
      const selectedGroupId = selectedMatchGroupBySeason[season.id];
      if (!data || !selectedGroupId) {
        return;
      }

      data.matches
        .filter(match => match.matchGroup.id === selectedGroupId)
        .forEach(match => {
          if (now < new Date(match.predictionsClose)) {
            ids.push(match.id);
          }
        });
    });

    return ids;
  }, [visibleSeasons, seasonMatchData, selectedMatchGroupBySeason]);

  const teamIdsFromVisibleMatches = useMemo(() => {
    const teamIds = new Set<string>();

    visibleSeasons.forEach(season => {
      const data = seasonMatchData[season.id];
      const selectedGroupId = selectedMatchGroupBySeason[season.id];
      if (!data || !selectedGroupId) {
        return;
      }

      data.matches
        .filter(match => match.matchGroup.id === selectedGroupId)
        .forEach(match => {
          teamIds.add(match.homeTeam.id);
          teamIds.add(match.awayTeam.id);
        });
    });

    return Array.from(teamIds).sort();
  }, [visibleSeasons, seasonMatchData, selectedMatchGroupBySeason]);

  const teamForms = useTeamForms(teamIdsFromVisibleMatches, Boolean(user));

  const cancelAutoSubmitRef = useRef<(matchId: string) => void>(() => {});

  const handlePredictionSubmit = useCallback(async (predictionId: string | null, matchId: string) => {
    const form = predictionForms[matchId];
    if (!form || !form.homeGoals || !form.awayGoals) {
      return;
    }

    // Validate that the scores are valid numbers
    const homeGoals = parseInt(form.homeGoals);
    const awayGoals = parseInt(form.awayGoals);
    
    if (isNaN(homeGoals) || isNaN(awayGoals) || homeGoals < 0 || awayGoals < 0) {
      alert('Please enter valid scores (0 or greater)');
      return;
    }

    setSubmitting(prev => ({ ...prev, [matchId]: true }));
    
    try {
      const predictionData = {
        match: matchId,
        homeGoals: homeGoals,
        awayGoals: awayGoals,
        isBanker: form.isBanker
      };

      // Create or update prediction
      if (predictionId) {
        await apiClient.put(`/predictions/${predictionId}`, predictionData);
      } else {
        await apiClient.post('/predictions', predictionData);
      }

      // Refresh predictions
      const predictionsResponse = await apiClient.get('/predictions');
      setPredictions(predictionsResponse.data);
      
      // Update the form with the new prediction data
      const updatedPrediction = predictionsResponse.data.find((p: Prediction) => p.match.id === matchId);
      if (updatedPrediction) {
        setPredictionForms(prev => ({
          ...prev,
          [matchId]: {
            homeGoals: updatedPrediction.homeGoals.toString(),
            awayGoals: updatedPrediction.awayGoals.toString(),
            isBanker: updatedPrediction.isBanker
          }
        }));
      }
      
      // Invalidate upcoming matches cache to refresh homepage
      invalidateUpcomingMatchesCache();

      cancelAutoSubmitRef.current(matchId);
      
    } catch (error: any) {
      console.error('Error updating prediction:', error);
      alert(error.response?.data?.message || 'Failed to update prediction');
    } finally {
      setSubmitting(prev => ({ ...prev, [matchId]: false }));
    }
  }, [predictionForms]);

  const submitPredictionByMatchId = useCallback((matchId: string) => {
    const prediction = predictions.find(p => p.match.id === matchId);
    handlePredictionSubmit(prediction?.id ?? null, matchId);
  }, [predictions, handlePredictionSubmit]);

  const { countdownValues, cancelAutoSubmit, hasActiveCountdown } = usePredictionAutoSubmit({
    predictionForms,
    predictions,
    submitting,
    onSubmit: submitPredictionByMatchId,
    eligibleMatchIds: upcomingMatchIds,
  });

  cancelAutoSubmitRef.current = cancelAutoSubmit;

  const handlePredictionChange = (matchId: string, field: 'homeGoals' | 'awayGoals' | 'isBanker', value: string | boolean) => {
    setPredictionForms(prev => {
      const existingForm = prev[matchId] || { homeGoals: '', awayGoals: '', isBanker: false };
      return {
        ...prev,
        [matchId]: {
          ...existingForm,
          [field]: value
        }
      };
    });
  };

  const handleBankerToggle = (
    matchId: string,
    matchIdsInWeek: string[],
    savedPredictions: typeof predictions
  ) => {
    setPredictionForms(prev => {
      const form = prev[matchId];
      const saved = savedPredictions.find(p => p.match.id === matchId);
      const current = form !== undefined ? Boolean(form.isBanker) : Boolean(saved?.isBanker);
      const newValue = !current;
      const updated = { ...prev };

      if (newValue) {
        matchIdsInWeek.forEach(id => {
          if (id === matchId) {
            updated[id] = {
              ...(updated[id] || { homeGoals: '', awayGoals: '', isBanker: false }),
              isBanker: true,
            };
          } else if (updated[id]) {
            updated[id] = { ...updated[id], isBanker: false };
          }
        });
      } else {
        updated[matchId] = {
          ...(updated[matchId] || { homeGoals: '', awayGoals: '', isBanker: false }),
          isBanker: false,
        };
      }

      return updated;
    });
  };

  if (loading) {
    return <MyPredictionsSkeleton />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-red-600 text-xl">{error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 w-full min-w-0">
        {/* Header */}
        <div className="mb-8">
          <Link to="/" className="mb-4 inline-block" style={{ color: 'var(--highlight)' }}>
            ← Back to Home
          </Link>
          <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>My Predictions</h1>
          <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>View and manage all your predictions</p>
        </div>



        {/* Stats */}
{/*         <MyPredictionsSummary
          showHeader={false}
          predictions={predictions}
          seasons={seasons}
          className="mb-6 bg-gray-50 dark:bg-gray-800/50 min-w-0 max-w-full"
        /> */}

        {/* Team Widget */}
        {/* <div className="mb-6">
          <TeamWidget />
        </div> */}

        {/* Season Tabs */}
        <div className="mb-6 -mx-4 px-4 sm:-mx-6 sm:px-6 overflow-hidden">
          <div className="relative min-w-0">
            {/* Left scroll button */}
            {seasons.length > 4 && seasonTabsScrollLeft > 0 && (
              <button
                onClick={() => {
                  const container = document.getElementById('season-tabs');
                  if (container) {
                    container.scrollBy({ left: -200, behavior: 'smooth' });
                  }
                }}
                className="absolute left-0 top-0 bottom-0 z-10 w-8 bg-gradient-to-r from-[#eff5f6] to-transparent dark:from-gray-900 dark:to-transparent flex items-center justify-center opacity-80 hover:opacity-100 transition-opacity"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}

            {/* Scrollable tabs container */}
            <div 
              id="season-tabs"
              className="flex gap-2 overflow-x-auto scrollbar-hide min-w-0 pr-6"
              onScroll={handleSeasonTabsScroll}
            >
              <button
                onClick={() => setSelectedSeason('all')}
                className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap flex-shrink-0 ${
                  selectedSeason === 'all'
                    ? 'btn-primary'
                    : 'btn-secondary'
                }`}
              >
                All Competitions
              </button>
              {seasons
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map((season) => (
                  <button
                    key={season.id}
                    onClick={() => setSelectedSeason(season.id)}
                    className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap flex-shrink-0 ${
                      selectedSeason === season.id
                        ? 'btn-primary'
                        : 'btn-secondary'
                    }`}
                  >
                    {season.name}
                  </button>
                ))}
            </div>

            {/* Right scroll button */}
            {seasons.length > 4 && (
              <button
                onClick={() => {
                  const container = document.getElementById('season-tabs');
                  if (container) {
                    container.scrollBy({ left: 200, behavior: 'smooth' });
                  }
                }}
                className="absolute right-0 top-0 bottom-0 z-10 w-8 bg-gradient-to-l from-[#eff5f6] to-transparent dark:from-gray-900 dark:to-transparent flex items-center justify-center opacity-80 hover:opacity-100 transition-opacity"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Knockout Stage Info */}
{/*         <div className="card rounded-lg shadow p-4 md:p-5 mb-6 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                Penalty shootouts are not included in prediction results. Matches that go to penalties are considered draws for prediction purposes, based on the final score before penalties.
              </p>
            </div>
          </div>
        </div> */}


        {/* Match Weeks */}
        <div className="space-y-6 min-w-0">
          {visibleSeasons.length === 0 ? (
            <div className="card rounded-lg shadow p-12 text-center">
              <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>No matchweeks found for the selected competition.</p>
              <Link 
                to="/seasons" 
                className="btn-primary inline-block mt-4 px-4 py-2 rounded-md"
              >
                Make Some Predictions
              </Link>
            </div>
          ) : (
            visibleSeasons.map(season => {
              const data = seasonMatchData[season.id];
              if (!data) return null;

              const categorized = categorizeMatchGroups(data.matchGroups, data.currentMatchGroup);
              const selectedMatchGroup = selectedMatchGroupBySeason[season.id] || data.currentMatchGroup?.id || '';
              const showPreviousMatchweeks = showPreviousMatchweeksBySeason[season.id] || false;
              const scrollArrows = matchGroupScrollArrows[season.id] || { left: false, right: false };
              const groupPredictions = seasonFilteredPredictions.filter(
                prediction => prediction.season.id === season.id && prediction.matchGroup.id === selectedMatchGroup
              );
              const groupMatches = data.matches.filter(match => match.matchGroup.id === selectedMatchGroup);
              const matchEntries = buildMatchWeekEntries(
                groupPredictions,
                groupMatches.length > 0 ? groupMatches : undefined
              );
              const sortedMatchEntries = [...matchEntries].sort((a, b) => {
                const dateComparison = new Date(a.match.matchDate).getTime() - new Date(b.match.matchDate).getTime();
                if (dateComparison !== 0) return dateComparison;
                return a.match.id.localeCompare(b.match.id);
              });
              const bankerMatchIdInWeek = getBankerMatchIdInWeek(
                groupMatches.map(match => match.id),
                groupPredictions,
                predictionForms
              );

              return (
              <div key={season.id} className="mb-8">
                {selectedSeason === 'all' && (
                  <div className="mb-4">
                    <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
                      {season.name}
                    </h2>
                    <div className="w-full h-px mt-2" style={{ backgroundColor: 'var(--border)' }}></div>
                  </div>
                )}

                <div className="mb-6 -mx-4 px-4 sm:-mx-6 sm:px-6 overflow-hidden">
                  <div className="relative min-w-0">
                  {scrollArrows.left && (
                    <button
                      onClick={() => {
                        const container = document.getElementById(`matchgroup-tabs-${season.id}`);
                        if (container) {
                          container.scrollBy({ left: -200, behavior: 'smooth' });
                          setTimeout(() => checkMatchGroupScrollPosition(season.id), 300);
                        }
                      }}
                      className="absolute left-0 top-0 bottom-0 z-10 w-8 bg-gradient-to-r from-[#eff5f6] to-transparent dark:from-gray-900 dark:to-transparent flex items-center justify-center opacity-80 hover:opacity-100 transition-opacity"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                  )}

                  <div
                    id={`matchgroup-tabs-${season.id}`}
                    className="flex gap-2 overflow-x-auto scrollbar-hide min-w-0 pr-6"
                    onScroll={() => checkMatchGroupScrollPosition(season.id)}
                  >
                    {categorized.old.length > 0 && (
                      <>
                        <button
                          onClick={() => setShowPreviousMatchweeksBySeason(prev => ({
                            ...prev,
                            [season.id]: !showPreviousMatchweeks,
                          }))}
                          className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap flex-shrink-0 flex items-center gap-1 ${
                            showPreviousMatchweeks ? 'btn-primary' : 'btn-secondary'
                          }`}
                        >
                          Previous Weeks
                          <svg
                            className={`w-4 h-4 transition-transform ${showPreviousMatchweeks ? '-rotate-90' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>

                        {showPreviousMatchweeks && categorized.old.map(group => (
                          <button
                            key={group.id}
                            onClick={() => setSelectedMatchGroupBySeason(prev => ({ ...prev, [season.id]: group.id }))}
                            className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap flex-shrink-0 ${
                              selectedMatchGroup === group.id ? 'btn-primary' : 'btn-secondary'
                            }`}
                          >
                            {group.name}
                          </button>
                        ))}
                      </>
                    )}

                    {categorized.previous && (
                      <button
                        onClick={() => setSelectedMatchGroupBySeason(prev => ({ ...prev, [season.id]: categorized.previous!.id }))}
                        className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap flex-shrink-0 ${
                          selectedMatchGroup === categorized.previous.id ? 'btn-primary' : 'btn-secondary'
                        }`}
                      >
                        {categorized.previous.name}
                      </button>
                    )}

                    {categorized.current && (
                      <button
                        onClick={() => setSelectedMatchGroupBySeason(prev => ({ ...prev, [season.id]: categorized.current!.id }))}
                        className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap flex-shrink-0 ${
                          selectedMatchGroup === categorized.current.id ? 'btn-primary' : 'btn-secondary'
                        }`}
                      >
                        {categorized.current.name}
                      </button>
                    )}

                    {categorized.future.map(group => (
                      <button
                        key={group.id}
                        onClick={() => setSelectedMatchGroupBySeason(prev => ({ ...prev, [season.id]: group.id }))}
                        className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap flex-shrink-0 ${
                          selectedMatchGroup === group.id ? 'btn-primary' : 'btn-secondary'
                        }`}
                      >
                        {group.name}
                      </button>
                    ))}
                  </div>

                  {scrollArrows.right && (
                    <button
                      onClick={() => {
                        const container = document.getElementById(`matchgroup-tabs-${season.id}`);
                        if (container) {
                          container.scrollBy({ left: 200, behavior: 'smooth' });
                          setTimeout(() => checkMatchGroupScrollPosition(season.id), 300);
                        }
                      }}
                      className="absolute right-0 top-0 bottom-0 z-10 w-8 bg-gradient-to-l from-[#eff5f6] to-transparent dark:from-gray-900 dark:to-transparent flex items-center justify-center opacity-80 hover:opacity-100 transition-opacity"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  )}
                  </div>
                </div>

                {sortedMatchEntries.length === 0 ? (
                  <EmptyStateCard
                    title="No matches available yet"
                    message="Fixtures for this matchweek aren't available yet. Check back soon, or pick another week to make your predictions."
                  />
                ) : (
                  <>
                      {/* Weekly Summary - Only show if there are scored matches */}
                      {(() => {
                        const scoredMatches = groupPredictions.filter(p => 
                          p.match.homeGoals !== undefined && p.match.homeGoals !== null && 
                          p.match.awayGoals !== undefined && p.match.awayGoals !== null
                        );
                        
                        if (scoredMatches.length === 0) return null;
                        
                        return (
                          <div className="mb-6 p-4 rounded-lg bg-gray-100 dark:bg-gray-700/50 border" style={{ borderColor: 'var(--border)' }}>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                              <div>
                                <div className="text-lg font-bold" style={{ color: 'var(--highlight)' }}>
                                  {scoredMatches.reduce((sum, p) => sum + (p.points || 0), 0)}
                                </div>
                                <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                                  Total Points
                                </div>
                              </div>
                              
                              <div>
                                <div className="text-lg font-bold" style={{ color: 'var(--highlight)' }}>
                                  {scoredMatches.filter(p => {
                                    const homeGoals = p.match.homeGoals!;
                                    const awayGoals = p.match.awayGoals!;
                                    const isExactScore = homeGoals === p.homeGoals && awayGoals === p.awayGoals;
                                    const isCorrectResult = !isExactScore && (
                                      (homeGoals > awayGoals && p.homeGoals > p.awayGoals) ||
                                      (homeGoals < awayGoals && p.homeGoals < p.awayGoals) ||
                                      (homeGoals === awayGoals && p.homeGoals === p.awayGoals)
                                    );
                                    
                                    return isExactScore || isCorrectResult;
                                  }).length}
                                </div>
                                <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                                  Correct Results
                                </div>
                              </div>
                              
                              <div>
                                <div className="text-lg font-bold" style={{ color: 'var(--highlight)' }}>
                                  {scoredMatches.filter(p => 
                                    p.match.homeGoals! === p.homeGoals && p.match.awayGoals! === p.awayGoals
                                  ).length}
                                </div>
                                <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                                  Exact Scores
                                </div>
                              </div>
                              
                              <div>
                                <div className="text-lg font-bold" style={{ color: 'var(--highlight)' }}>
                                  {scoredMatches.length}
                                </div>
                                <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                                  Scored Matches
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 min-w-0">
                        {sortedMatchEntries.map(({ match, prediction }) => {
                        const status = getMatchStatusForEntry({ match, prediction });
                        const predictionsOpen = status === 'upcoming';
                        const outcome = getPredictionOutcome(
                          match.homeGoals,
                          match.awayGoals,
                          prediction,
                          predictionsOpen
                        );
                        const scored = isScoredOutcome(outcome);
                        const accentClass = scored ? getOutcomeAccentColor(outcome) : '';
                        const mutedClass = scored ? getOutcomeMutedTextColor(outcome) : '';
                        
                        return (
                          <div key={match.id} className={`card rounded-lg p-3 md:p-4 ${scored ? 'max-md:pb-6 md:min-h-[420px]' : 'min-h-[420px]'} relative overflow-hidden min-w-0 ${getOutcomeCardClasses(outcome, Boolean(prediction))}`}>
                            {(() => {
                              const statusPill = getMatchCardStatusPill(match, prediction, status);
                              return statusPill ? (
                                <div className="absolute top-3 sm:top-5 right-2 sm:right-3">
                                  {statusPill}
                                </div>
                              ) : null;
                            })()}

                            
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 space-y-2 sm:space-y-0">
                              <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                                <div
                                  className={`text-sm ${mutedClass}`}
                                  style={!scored ? { color: 'var(--text-secondary)' } : undefined}
                                >
                                  {formatDate(match.matchDate)}
                                </div>

                              </div>
                            </div>

                            {/* Team display section - matches SeasonDetail exactly */}
                            <div className="text-center pt-4 mb-4">
                              <div className="grid grid-cols-3 gap-2 sm:gap-4 items-center min-w-0">
                                <div className="text-center min-w-0">
                                  {match.homeTeam.logoUrl && (
                                    <img 
                                      src={getTeamLogoUrl(match.homeTeam.logoUrl)}
                                      alt={`${match.homeTeam.name} logo`}
                                      className="w-10 h-10 object-contain justify-center mx-auto mb-2"
                                    />
                                  )}
                                  <div className={`font-semibold text-xs sm:text-sm break-words leading-tight px-0.5 ${accentClass}`} style={!scored ? { color: 'var(--text-primary)' } : undefined}>{match.homeTeam.name}</div>
                                  {user && <TeamFormIndicator teamId={match.homeTeam.id} teamForms={teamForms} />}
                                </div>
                                
                                <div className="text-center min-w-0">
                                  {matchHasResult(match.homeGoals, match.awayGoals) ? (
                                    <div>
                                      <div className={`text-2xl sm:text-3xl font-bold tabular-nums ${accentClass}`} style={!scored ? { color: 'var(--text-primary)' } : undefined}>
                                        {match.homeGoals} - {match.awayGoals}
                                      </div>
                                      <div
                                        className={`text-xs sm:text-sm font-bold uppercase tracking-wider mt-1 ${scored ? mutedClass : ''}`}
                                        style={!scored ? { color: '#22c55e' } : undefined}
                                      >
                                        {scored ? 'Full time' : 'Final result'}
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="text-xl font-bold" style={{ color: 'var(--text-secondary)' }}>vs</div>
                                  )}
                                </div>
                                
                                <div className="text-center min-w-0">
                                  {match.awayTeam.logoUrl && (
                                    <img 
                                      src={getTeamLogoUrl(match.awayTeam.logoUrl)}
                                      alt={`${match.awayTeam.name} logo`}
                                      className="w-10 h-10 object-contain justify-center mx-auto mb-2"
                                    />
                                  )}
                                  <div className={`font-semibold text-xs sm:text-sm break-words leading-tight px-0.5 ${accentClass}`} style={!scored ? { color: 'var(--text-primary)' } : undefined}>{match.awayTeam.name}</div>
                                  {user && <TeamFormIndicator teamId={match.awayTeam.id} teamForms={teamForms} />}
                                </div>
                              </div>
                            </div>

                            {/* Prediction form section - matches SeasonDetail exactly */}
                            <div className="text-center">
                              {status === 'upcoming' ? (
                                // Always show prediction form for upcoming matches
                                <div className="space-y-2">
                                  <div className="grid grid-cols-3 gap-2 sm:gap-4 items-center mb-4 min-w-0">
                                    <div className="text-center">
                                      <ScoreInput
                                        value={predictionForms[match.id]?.homeGoals || ''}
                                        onChange={(value) => handlePredictionChange(match.id, 'homeGoals', value)}
                                        min={0}
                                        max={9}
                                      />
                                    </div>
                                    
                                    <div className="text-center text-2xl font-bold" style={{ color: 'var(--text-secondary)' }}>
                                      -
                                    </div>
                                    
                                    <div className="text-center">
                                      <ScoreInput
                                        value={predictionForms[match.id]?.awayGoals || ''}
                                        onChange={(value) => handlePredictionChange(match.id, 'awayGoals', value)}
                                        min={0}
                                        max={9}
                                      />
                                    </div>
                                  </div>
                                  
                                  {(!bankerMatchIdInWeek || bankerMatchIdInWeek === match.id) && (
                                    <BankerButton
                                      isActive={
                                        predictionForms[match.id] !== undefined
                                          ? Boolean(predictionForms[match.id]?.isBanker)
                                          : Boolean(prediction?.isBanker)
                                      }
                                      onToggle={() => handleBankerToggle(
                                        match.id,
                                        groupMatches.map(groupMatch => groupMatch.id),
                                        groupPredictions
                                      )}
                                      tooltipText="Banker predictions earn double points if correct, but lose 1 point if incorrect"
                                    />
                                  )}
                                  
                                  <div className="flex flex-wrap items-center justify-center gap-2">
                                    {(() => {
                                      const form = predictionForms[match.id];
                                      const hasValidForm = Boolean(form?.homeGoals !== '' && form?.awayGoals !== '');
                                      const changed = prediction
                                        ? hasValidForm && (
                                            form.homeGoals !== prediction.homeGoals.toString() ||
                                            form.awayGoals !== prediction.awayGoals.toString() ||
                                            form.isBanker !== prediction.isBanker
                                          )
                                        : hasValidForm;
                                      const countdownValue = countdownValues[match.id];

                                      if (!changed) {
                                        return null;
                                      }

                                      return (
                                        <div className="flex flex-col items-center space-y-2 w-full">
                                          <div className="flex flex-wrap items-center justify-center gap-2">
                                            <button
                                              onClick={() => {
                                                if (!prediction) {
                                                  cancelAutoSubmit(match.id);
                                                }
                                                handlePredictionSubmit(prediction?.id ?? null, match.id);
                                              }}
                                              disabled={submitting[match.id] || !form?.homeGoals || !form?.awayGoals}
                                              className="px-4 py-2 text-sm rounded btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                              {submitting[match.id] ? 'Saving...' : (prediction ? 'Update Prediction' : 'Save Prediction')}
                                            </button>
                                            {prediction && (
                                              <button
                                                onClick={() => {
                                                  setPredictionForms(prev => ({
                                                    ...prev,
                                                    [match.id]: {
                                                      homeGoals: prediction.homeGoals.toString(),
                                                      awayGoals: prediction.awayGoals.toString(),
                                                      isBanker: prediction.isBanker
                                                    }
                                                  }));
                                                }}
                                                className="px-4 py-2 text-sm rounded btn-secondary"
                                              >
                                                Reset
                                              </button>
                                            )}
                                          </div>

                                          {!prediction && hasActiveCountdown(match.id) && countdownValue && (
                                            <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                                              Auto-submitting in {countdownValue} second{countdownValue !== 1 ? 's' : ''}...
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </div>
                              ) : (
                                <div className="space-y-3">
                                  {scored && prediction && (
                                    <ScoredMatchSummary
                                      prediction={prediction}
                                      outcome={outcome as 'exact' | 'correct' | 'wrong'}
                                    />
                                  )}

                                  {outcome === 'missed' && <MissedMatchSummary />}

                                  {outcome === 'predicted' && prediction && status !== 'upcoming' && (
                                    <PendingPredictionSummary prediction={prediction} />
                                  )}
                                </div>
                              )}
                            </div>

                            {status !== 'upcoming' && match.status !== 'finished' && !(prediction && outcome === 'predicted') && (
                              <div className="text-sm text-center mt-2" style={{ color: 'var(--text-secondary)' }}>
                                Predictions closed
                              </div>
                            )}

                            {prediction?.pointsBreakdown && (
                              <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--border)' }}>
                                <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>Points Breakdown:</div>
                                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
                                  <span>Exact Score: {prediction.pointsBreakdown.exactScore}</span>
                                  <span>Correct Result: {prediction.pointsBreakdown.correctResult}</span>
                                  <span>Banker Bonus: {prediction.pointsBreakdown.bankerBonus}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      </div>
                      
                      {/* View All Fixtures Link */}
                      <div className="text-center mt-6 pt-6 border-t" style={{ borderColor: 'var(--border)' }}>
                        <Link
                          to={`/seasons/${season.id}?matchweek=${selectedMatchGroup}`}
                          className="inline-flex flex-wrap items-center justify-center px-4 py-3 text-sm font-medium rounded-md btn-secondary hover:opacity-80 transition-opacity max-w-full text-center"
                        >

                          <svg className="w-6 h-6 mr-2" width="24"  height="24"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor" ><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 12m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" /><path d="M3 9h3v6h-3z" /><path d="M18 9h3v6h-3z" /><path d="M3 5m0 2a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2z" /><path d="M12 5l0 14" /></svg>

                          View All Fixtures For This Matchweek
                        </Link>
                      </div>
                    </>
                )}
              </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default MyPredictions;